import React, { useState } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import UsersManagement from "../components/dashboard/UsersManagement";
import ContactsManagement from "../components/dashboard/ContactsManagement";
import CompaniesManagement from "../components/dashboard/CompaniesManagement";
import VacanciesManagement from "../components/dashboard/VacanciesManagement";
import DevManagement from "../components/dashboard/DevManagement";
import UserProfileDropdown from "../components/common/UserProfileDropdown";
import SettingsModal from "../components/common/SettingsModal";
import ContactDetails from "../components/details/ContactDetails";
import CompanyDetails from "../components/details/CompanyDetails";
import UserDetails from "../components/details/UserDetails";
import ScraperControl from "./ScraperControl";
import GlobalDraggableScraperStatus from "../components/GlobalDraggableScraperStatus";
import BulkURLImport from "../components/common/BulkURLImport";
import { useScraperContext } from "../contexts/ScraperContext";

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 8px;
`;

const Header = styled.header`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e5e7eb;
`;

const TabList = styled.div`
  display: flex;
  padding: 0 32px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 16px 24px;
  font-size: 1rem;
  font-weight: 500;
  color: ${(props) => (props.$active ? "#667eea" : "#6b7280")};
  border-bottom: 2px solid
    ${(props) => (props.$active ? "#667eea" : "transparent")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #667eea;
  }
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 0 0 12px 12px;
  padding: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e1e8ed;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

type TabType =
  | "users"
  | "contacts"
  | "companies"
  | "vacancies"
  | "import"
  | "scraper"
  | "dev";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleHideModal } = useScraperContext();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showSettings, setShowSettings] = useState(false);
  const [showScraperModal, setShowScraperModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSsoMode, setIsSsoMode] = useState(false);

  React.useEffect(() => {
    // Check if we are running in an iframe or if SSO token was present
    const inIframe = window.self !== window.top;
    setIsSsoMode(inIframe);
  }, []);

  // All users can access the Users tab (view-only for regular users)

  // Determine active tab based on current route
  const getActiveTab = (): TabType => {
    const path = location.pathname;
    if (path.includes("/users")) return "users";
    if (path.includes("/contacts")) return "contacts";
    if (path.includes("/companies")) return "companies";
    if (path.includes("/vacancies")) return "vacancies";
    if (path.includes("/import")) return "import";
    if (path.includes("/scraper")) return "scraper";
    if (path.includes("/dev")) return "dev";
    return "contacts";
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tab: TabType) => {
    navigate(`/dashboard/${tab}`);
  };

  return (
    <DashboardContainer>
      {!isSsoMode && (
        <Header>
          <Title>Lead Me</Title>
          <UserInfo>
            <UserProfileDropdown onOpenSettings={() => setShowSettings(true)} />
          </UserInfo>
        </Header>
      )}

      <TabContainer>
        <TabList>
          <Tab
            $active={activeTab === "contacts"}
            onClick={() => handleTabChange("contacts")}
          >
            Contatti
          </Tab>
          <Tab
            $active={activeTab === "companies"}
            onClick={() => handleTabChange("companies")}
          >
            Aziende
          </Tab>
          <Tab
            $active={activeTab === "vacancies"}
            onClick={() => handleTabChange("vacancies")}
          >
            Posizioni
          </Tab>
          <Tab
            $active={activeTab === "users"}
            onClick={() => handleTabChange("users")}
          >
            Commerciali
          </Tab>
          <Tab
            $active={activeTab === "scraper"}
            onClick={() => handleTabChange("scraper")}
          >
            🤖 Scraper
          </Tab>
          {isAdmin && (
            <Tab
              $active={activeTab === "dev"}
              onClick={() => handleTabChange("dev")}
            >
              🛠️ Dev
            </Tab>
          )}
        </TabList>
      </TabContainer>

      <ContentArea>
        <Routes>
          <Route path="/" element={<Navigate to="contacts" replace />} />
          <Route path="/contacts" element={<ContactsManagement />} />
          <Route path="/contacts/new" element={<ContactDetails />} />
          <Route path="/contacts/:id" element={<ContactDetails />} />
          <Route path="/companies" element={<CompaniesManagement />} />
          <Route path="/companies/new" element={<CompanyDetails />} />
          <Route path="/companies/:id" element={<CompanyDetails />} />
          <Route path="/vacancies" element={<VacanciesManagement />} />
          <Route path="/scraper" element={<ScraperControl />} />
          {isAdmin && <Route path="/dev" element={<DevManagement />} />}
          <Route path="/users" element={<UsersManagement />} />
          <Route path="/users/:id" element={<UserDetails />} />
        </Routes>
      </ContentArea>

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Global Scraper Modal */}
      {showScraperModal && (
        <ModalOverlay onClick={() => !isProcessing && setShowScraperModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>🚀 Importazione Massiva Contatti</ModalTitle>
              <ModalCloseButton
                onClick={() => !isProcessing && setShowScraperModal(false)}
                disabled={isProcessing}
              >
                ×
              </ModalCloseButton>
            </ModalHeader>
            <BulkURLImport
              onSuccess={() => setShowScraperModal(false)}
              onCancel={() => setShowScraperModal(false)}
              onHide={() => {
                handleHideModal();
                setShowScraperModal(false);
              }}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
            />
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Global Draggable Scraper Status */}
      <GlobalDraggableScraperStatus />
    </DashboardContainer>
  );
};

export default Dashboard;
